import type { ClientCatalogProps } from "@core/SitePresenter/SitePresenter";
import type ApiUrlCreator from "@core-ui/ApiServices/ApiUrlCreator";
import ArticleViewService from "@core-ui/ContextServices/views/articleView/ArticleViewService";
import t from "@ext/localization/locale/translate";
import PrintView from "@ext/print/components/PrintView";
import { useExportPdf } from "@ext/print/components/useExportPdf";
import { usePdfFile } from "@ext/print/components/usePdfFile";
import type { PdfPrintParams } from "@ext/print/types";
import { nextFrame } from "@ext/print/utils/pagination/scheduling";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, AlertIcon, AlertTitle } from "@ui-kit/Alert";
import { Button } from "@ui-kit/Button";
import { CheckboxField } from "@ui-kit/Checkbox";
import { Collapsible, CollapsibleContent } from "@ui-kit/Collapsible";
import { Dialog, DialogBody, DialogContent } from "@ui-kit/Dialog";
import { Form, FormField, FormFooter, FormHeader, FormStack } from "@ui-kit/Form";
import { Loader } from "@ui-kit/Loader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@ui-kit/Select";
import { type DOMAttributes, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

interface ExportPdfProps {
	onClose?: () => void;
	catalogProps: ClientCatalogProps;
	itemRefPath: string;
	isCategory: boolean;
	title: string;
	apiUrlCreator: ApiUrlCreator;
	templates: string[];
}

const localStorageProvider = {
	getProps: (): PdfPrintParams => {
		const props = localStorage.getItem("exportPdfProps") as unknown as string;
		const defaults = { titlePage: false, tocPage: false, titleNumber: false, articleTitle: true };
		return props ? { ...defaults, ...JSON.parse(props) } : defaults;
	},
	setProps: (props: PdfPrintParams) => {
		localStorage.setItem("exportPdfProps", JSON.stringify(props));
	},
};

const ExportPdf = (props: ExportPdfProps) => {
	const { templates, onClose, isCategory, catalogProps, itemRefPath, title, apiUrlCreator } = props;
	const savedProps = localStorageProvider.getProps();
	const defaultValues = {
		...savedProps,
		template: templates.includes(savedProps.template) ? savedProps.template : undefined,
	};
	const pdfFile = usePdfFile(title);
	const {
		open,
		isExporting,
		isChoosingDestination,
		chooseDestination,
		progressLabel,
		handleProgress,
		handleComplete,
		handleError,
		onOpenChange,
		setIsExporting,
		cancelTaskRef,
	} = useExportPdf({ onClose, savesToFile: pdfFile !== null });
	const exportAbortRef = useRef<AbortController | null>(null);
	const type = !itemRefPath ? "catalog" : isCategory ? "category" : "article";
	const [useTemplate, setUseTemplate] = useState(Boolean(defaultValues.template));

	const schema = z.object({
		titlePage: z.boolean(),
		tocPage: z.boolean(),
		titleNumber: z.boolean(),
		articleTitle: z.boolean(),
		template: z.string().optional(),
	});

	const form = useForm<z.infer<typeof schema>>({
		resolver: zodResolver(schema),
		defaultValues,
		mode: "onChange",
	});

	const documentOptions = [
		{
			name: "titlePage" as const,
			label: t("export.pdf.form.titlePage"),
			description: t("export.pdf.form.titlePageDescription"),
		},
		{
			name: "tocPage" as const,
			label: t("export.pdf.form.tocPage"),
			description: t("export.pdf.form.tocPageDescription"),
		},
		{
			name: "titleNumber" as const,
			label: t("export.pdf.form.titleNumber"),
			description: t("export.pdf.form.titleNumberDescription"),
		},
	];

	// A title page would repeat the article's title, a table of contents would list only it, numbering numbers articles.
	const articleOptions = [
		{
			name: "articleTitle" as const,
			label: t("export.pdf.form.articleTitle"),
			description: t("export.pdf.form.articleTitleDescription"),
		},
	];

	const checkboxConfig = type === "article" ? articleOptions : documentOptions;

	const exportPdf = form.handleSubmit(async (params: PdfPrintParams) => {
		const destination =
			pdfFile &&
			(await chooseDestination(() => pdfFile.chooseDestination(() => void exportPdf())).catch(handleError));
		if (pdfFile && !destination) return;

		const template = useTemplate && templates.includes(params.template) ? params.template : undefined;
		const exportParams: PdfPrintParams =
			type === "article"
				? { titlePage: false, tocPage: false, titleNumber: false, articleTitle: params.articleTitle, template }
				: { ...params, articleTitle: true, template };
		exportAbortRef.current?.abort();
		cancelTaskRef.current?.();

		const exportController = new AbortController();
		exportAbortRef.current = exportController;

		localStorageProvider.setProps({ ...params, template });
		setIsExporting(true);
		handleProgress({ stage: "exporting", ratio: 0 });
		await nextFrame();
		ArticleViewService.setBottomView(() => (
			<PrintView
				apiUrlCreator={apiUrlCreator}
				catalogProps={catalogProps}
				exportSignal={exportController.signal}
				isCategory={isCategory}
				itemPath={itemRefPath}
				onCancelRef={(fn) => {
					if (!fn) {
						cancelTaskRef.current = null;
						exportAbortRef.current = null;
						return;
					}

					cancelTaskRef.current = () => {
						const controller = exportAbortRef.current;
						if (controller && !controller.signal.aborted) {
							controller.abort();
						}
						fn();
					};
				}}
				onComplete={
					destination
						? () => {
								handleComplete();
								pdfFile.showSaved(destination);
							}
						: handleComplete
				}
				onError={handleError}
				onProgress={handleProgress}
				params={exportParams}
				print={destination ? () => pdfFile.save(destination) : undefined}
			/>
		));
	});

	const formSubmit: DOMAttributes<HTMLFormElement>["onSubmit"] = (event) => {
		if (isExporting) {
			event?.preventDefault?.();
			return;
		}

		void exportPdf(event);
	};

	return (
		<Dialog className="print:hidden" onOpenChange={onOpenChange} open={open}>
			<DialogContent data-modal-root>
				<Form asChild {...form}>
					<form onSubmit={formSubmit}>
						<FormHeader
							description={
								<div>
									{t("export.pdf.form.description")}{" "}
									<a
										href="https://gram.ax/resources/docs/collaboration/export-docx-pdf/app"
										rel="noreferrer"
										target="_blank"
									>
										{t("more")}
									</a>
								</div>
							}
							icon="file-text"
							title={t(`export.pdf.form.title.${type}`)}
						/>
						<DialogBody>
							<FormStack>
								<div className="flex flex-col gap-6">
									<div className="space-y-3">
										<div className="space-y-5 lg:space-y-4">
											{checkboxConfig.map((cfg) => (
												<Controller
													control={form.control}
													key={cfg.name}
													name={cfg.name}
													render={({ field: { value, onChange, name } }) => (
														<CheckboxField
															checked={!!value}
															className="items-start"
															description={cfg.description}
															disabled={isExporting}
															label={cfg.label}
															name={name}
															onCheckedChange={(checked) => onChange(!!checked)}
														/>
													)}
												/>
											))}
											{templates?.length > 0 && (
												<CheckboxField
													checked={useTemplate}
													className="items-start"
													description={t("export.pdf.form.templateDescription.body")}
													disabled={isExporting}
													label={t("export.pdf.form.template")}
													name="useTemplate"
													onCheckedChange={(checked) => {
														const shouldUseTemplate = !!checked;
														setUseTemplate(shouldUseTemplate);
														if (!shouldUseTemplate) form.setValue("template", undefined);
													}}
												/>
											)}
										</div>

										{templates?.length > 0 && (
											<Collapsible open={useTemplate}>
												<CollapsibleContent>
													<FormField
														control={({ field }) => (
															<Select
																onValueChange={field.onChange}
																value={field.value || undefined}
															>
																<SelectTrigger
																	disabled={isExporting}
																	onClear={
																		field.value
																			? () => field.onChange(undefined)
																			: undefined
																	}
																	type="button"
																>
																	<SelectValue placeholder={t("no-selected")} />
																</SelectTrigger>
																<SelectContent>
																	{templates.map((template) => (
																		<SelectItem
																			data-qa={"qa-clickable"}
																			key={template}
																			value={template}
																		>
																			{template}
																		</SelectItem>
																	))}
																</SelectContent>
															</Select>
														)}
														description={
															<div>
																{t("export.pdf.form.templateDescription.body")}{" "}
																<a
																	href="https://gram.ax/resources/docs/collaboration/export-docx-pdf/add-custom-template/pdf"
																	rel="noreferrer"
																	target="_blank"
																>
																	{t("more")}
																</a>
															</div>
														}
														layout="vertical"
														name="template"
														title={t("export.pdf.form.template")}
													/>
												</CollapsibleContent>
											</Collapsible>
										)}
									</div>
									{!pdfFile && (
										<Alert>
											<AlertIcon icon="file" />
											<AlertTitle className="font-normal">
												{t("export.pdf.form.printDialog")}
											</AlertTitle>
										</Alert>
									)}
								</div>
							</FormStack>
						</DialogBody>
						<FormFooter
							className="flex flex-col gap-4"
							primaryButton={
								<Button
									disabled={isExporting || isChoosingDestination}
									startIcon={!isExporting ? (pdfFile ? "download" : "printer") : null}
									type="submit"
									variant="primary"
								>
									{!isExporting ? (
										t(pdfFile ? "export.pdf.form.savePdf" : "export.pdf.form.openPrint")
									) : (
										<div className="flex flex-row items-center">
											<Loader className="pl-1 text-inverse" />
											<span className="text-sm">{progressLabel}</span>
										</div>
									)}
								</Button>
							}
							secondaryButton={
								<Button onClick={() => onOpenChange(false)} type="button" variant="outline">
									{t("close")}
								</Button>
							}
						/>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
};

export default ExportPdf;
